import { useEffect, useState, useRef } from "react";
import { Plus } from "lucide-react";

import ItemList from "../features/item/ItemList";
import ItemDrawer from "../features/item/ItemDrawer";
import { itemService } from "../services/item.service";

import UndoToast from "../ui/UndoToast";
import DropdownMenu from "../ui/DropdownMenu";
import ConfirmDeleteModal from "../ui/ConfirmDeleteModal";

export default function ItemsPage({ onboardingContext }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // 🔔 Undo toast
  const [archivedItem, setArchivedItem] = useState(null);
  const [showToast, setShowToast] = useState(false);

  const [status, setStatus] = useState("all");
  const [statusAnchor, setStatusAnchor] = useState(null);

  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteAllArchived, setShowDeleteAllArchived] = useState(false);

  const [priceAnchor, setPriceAnchor] = useState(null);
  const [priceRange, setPriceRange] = useState({
    min: "",
    max: "",
  });

  const searchRef = useRef(null);

  /* ─────────────────────────────
     LOAD ITEMS
  ───────────────────────────── */
  useEffect(() => {
    loadItems();
  }, []);

  // Handle command launcher or onboarding deep-link
  useEffect(() => {
    if (onboardingContext?.action === 'create') {
      setSelectedItem(null);
      setOpen(true);
    }
  }, [onboardingContext]);

  async function loadItems() {
    const data = await itemService.list();
    setItems(data);
  }

  /* ─────────────────────────────
     ARCHIVE / RESTORE / DELETE
  ───────────────────────────── */
  async function handleArchive(item) {
    // optimistic
    setItems((prev) =>
      prev.map((i) =>
        i.id === item.id ? { ...i, archived_at: new Date().toISOString() } : i
      )
    );

    setArchivedItem(item);
    setShowToast(true);

    try {
      await itemService.archive(item.id);
    } catch (err) {
      console.error("Archive failed", err);
      loadItems();
    }
  }

  async function handleRestore(item) {
    setItems((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, archived_at: null } : i))
    );

    try {
      await itemService.restore(item.id);
    } catch (err) {
      console.error("Restore failed", err);
      loadItems();
    }
  }

  function confirmDelete(item) {
    setDeleteTarget(item);
  }

  async function handleDeleteConfirmed() {
    if (!deleteTarget) return;

    const item = deleteTarget;

    setItems((prev) => prev.filter((i) => i.id !== item.id));
    setDeleteTarget(null);

    try {
      await itemService.delete(item.id);
    } catch (err) {
      console.error("Delete failed", err);
      setItems((prev) => [item, ...prev]);
    }
  }

  async function handleDeleteAllArchivedConfirmed() {
    const archivedCount = items.filter((i) => i.archived_at).length;

    if (archivedCount === 0) {
      setShowDeleteAllArchived(false);
      return;
    }

    // Optimistic UI update
    setItems((prev) => prev.filter((i) => !i.archived_at));
    setShowDeleteAllArchived(false);

    try {
      await itemService.deleteAllArchived();
    } catch (err) {
      console.error("Delete all archived failed", err);
      loadItems(); // Reload on error
    }
  }

  function handleUndo() {
    if (!archivedItem) return;

    setItems((prev) =>
      prev.map((i) =>
        i.id === archivedItem.id ? { ...i, archived_at: null } : i
      )
    );

    setArchivedItem(null);
    setShowToast(false);
  }

  async function handlePriceUpdate(item, newPrice) {
    // 1️⃣ Optimistic UI update (instant feedback)
    setItems((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, price: newPrice } : i))
    );

    try {
      // 2️⃣ Persist to DB
      await itemService.update(item.id, {
        ...item,
        price: newPrice,
      });
    } catch (err) {
      console.error("Inline price update failed", err);

      // 3️⃣ Rollback if backend fails
      loadItems();
    }
  }

  /* ─────────────────────────────
     FILTERING + SORTING
  ───────────────────────────── */
  const archivedCount = items.filter((i) => i.archived_at).length;

  const visibleItems = items
    // 🔍 SEARCH
    .filter((item) => {
      const q = search.toLowerCase().trim();
      if (!q) return true;

      return Object.values(item)
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    })

    // 📌 STATUS
    .filter((item) => {
      if (status === "active") return !item.archived_at;
      if (status === "archived") return !!item.archived_at;
      return true;
    })

    // 💰 PRICE RANGE
    .filter((item) => {
      const price = Number(item.price || 0);

      const min = priceRange.min !== "" ? Number(priceRange.min) : null;

      const max = priceRange.max !== "" ? Number(priceRange.max) : null;

      if (min !== null && price < min) return false;
      if (max !== null && price > max) return false;

      return true;
    })

    // ↕ SORT
    .sort((a, b) => {
      if (!!a.archived_at !== !!b.archived_at) {
        return a.archived_at ? 1 : -1;
      }
      return new Date(b.created_at) - new Date(a.created_at);
    });

  /* ⌘K search shortcut */
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="space-y-4 relative max-w-full">
      {/* ───────── TOP CONTROLS ───────── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* Left */}
        <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
          <input
            ref={searchRef}
            placeholder="Search items…  ⌘K"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="
              w-full max-w-[260px]
              px-3 py-2
              text-sm
              rounded-md
              border border-[var(--color-border)]
              focus:outline-none
              focus:ring-1 focus:ring-[var(--color-primary)]/40
            "
          />

          <button
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setStatusAnchor(rect);
            }}
            className="
    px-3 py-2
    text-sm
    rounded-md
    border border-[var(--color-border)]
    bg-white
    flex items-center justify-between
    min-w-[140px]
    hover:bg-[var(--color-bg-muted)]
    flex-shrink-0
  "
          >
            {status === "all"
              ? "All"
              : status === "active"
              ? "Active"
              : "Archived"}
          </button>

          <button
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setPriceAnchor(rect);
            }}
            className="
    px-3 py-2
    text-sm
    rounded-md
    border border-[var(--color-border)]
    bg-white
    min-w-[140px]
    hover:bg-[var(--color-bg-muted)]
    flex-shrink-0
  "
          >
            {priceRange.min || priceRange.max
              ? `₹ ${priceRange.min || "0"} – ${priceRange.max || "∞"}`
              : "Price"}
          </button>
        </div>

        {/* Right */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => {
              setSelectedItem(null);
              setOpen(true);
            }}
            className="
              flex items-center gap-1.5
              px-3 py-2
              rounded-md
              bg-[var(--color-primary)]
              text-white
              text-sm font-medium
            "
          >
            <Plus size={16} />
            Add Item
          </button>

          {/* Delete All Archived Button */}
          {archivedCount > 0 && (
            <button
              onClick={() => setShowDeleteAllArchived(true)}
              className="
                flex items-center gap-1.5
                px-3 py-2
                rounded-md
                bg-red-600
                text-white
                text-sm font-medium
                hover:bg-red-700
                transition-colors
                whitespace-nowrap
              "
            >
              Delete All Archived ({archivedCount})
            </button>
          )}
        </div>
      </div>

      {/* ───────── LIST ───────── */}
      <ItemList
        items={visibleItems}
        onSelect={(item) => {
          setSelectedItem(item);
          setOpen(true);
        }}
        onArchive={handleArchive}
        onRestore={handleRestore}
        onDelete={confirmDelete}
        onPriceUpdate={handlePriceUpdate}
      />

      {/* Drawer (later step) */}
      {open && (
        <ItemDrawer
          item={selectedItem}
          onClose={() => setOpen(false)}
          onSaved={loadItems}
        />
      )}

      {/* Undo toast */}
      {showToast && archivedItem && (
        <UndoToast
          message={`Item "${archivedItem.name}" archived`}
          onUndo={handleUndo}
          onClose={() => {
            setShowToast(false);
            setArchivedItem(null);
          }}
        />
      )}

      {statusAnchor && (
        <DropdownMenu
          anchorRect={statusAnchor}
          onClose={() => setStatusAnchor(null)}
        >
          {[
            { key: "all", label: "All" },
            { key: "active", label: "Active" },
            { key: "archived", label: "Archived" },
          ].map((item) => (
            <button
              key={item.key}
              onClick={() => {
                setStatus(item.key);
                setStatusAnchor(null);
              }}
              className={`
                 w-full
                 px-3 py-2
                 text-left
                 hover:bg-[var(--color-bg-muted)]
                 ${
                   status === item.key
                     ? "text-[var(--color-primary)] font-medium"
                     : ""
                 }
               `}
            >
              {item.label}
            </button>
          ))}
        </DropdownMenu>
      )}

      {priceAnchor && (
  <DropdownMenu
    anchorRect={priceAnchor}
    onClose={() => setPriceAnchor(null)}
  >
    <div
      className="
        w-[320px]          /* 👈 increased width */
        px-4 py-3
        space-y-3
      "
    >
      {/* Header */}
      <div className="text-xs font-medium text-[var(--color-text-muted)]">
        Price range
      </div>

      {/* Inputs */}
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          placeholder="Min ₹"
          value={priceRange.min}
          onChange={(e) =>
            setPriceRange((r) => ({ ...r, min: e.target.value }))
          }
          className="
            input
            h-9
            text-sm
          "
        />

        <input
          type="number"
          placeholder="Max ₹"
          value={priceRange.max}
          onChange={(e) =>
            setPriceRange((r) => ({ ...r, max: e.target.value }))
          }
          className="
            input
            h-9
            text-sm
          "
        />
      </div>

      {/* Clear */}
      {(priceRange.min || priceRange.max) && (
        <button
          onClick={() => setPriceRange({ min: "", max: "" })}
          className="
            text-xs
            text-red-600
            hover:underline
            self-start
          "
        >
          Clear filter
        </button>
      )}
    </div>
  </DropdownMenu>
)}


      <ConfirmDeleteModal
        open={!!deleteTarget}
        title={`Delete "${deleteTarget?.name}" permanently?`}
        description="This action cannot be undone. The item will be permanently removed."
        confirmLabel="Delete permanently"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirmed}
      />

      <ConfirmDeleteModal
        open={showDeleteAllArchived}
        title={`Delete all ${archivedCount} archived items permanently?`}
        description="This action cannot be undone. All archived items will be permanently removed from the database."
        confirmLabel={`Delete ${archivedCount} items permanently`}
        onCancel={() => setShowDeleteAllArchived(false)}
        onConfirm={handleDeleteAllArchivedConfirmed}
      />
    </div>
  );
}
